import z from 'zod';

import { SeatType } from '@/store/people/people.types';

/**
 * People API `subscription` tag.
 *
 * A subscription belongs to a *service* (vessel), not a person: one service has at
 * most one subscription row, whatever its state.
 */

// `harbor` is the free default and has no Stripe object behind it. It never appears
// in a request enum — only navigate/command are purchasable.
export enum SubscriptionTier {
  HARBOR = 'harbor',
  NAVIGATE = 'navigate',
  COMMAND = 'command',
}

export type PaidSubscriptionTier = SubscriptionTier.NAVIGATE | SubscriptionTier.COMMAND;

export const PAID_TIERS = [SubscriptionTier.NAVIGATE, SubscriptionTier.COMMAND] as const;

export enum BillingInterval {
  MONTHLY = 'monthly',
  ANNUALLY = 'annually',
}

/**
 * Not declared as an enum by the spec — `payment_status` comes back as a bare string
 * and only `incomplete` is documented. These are exactly the values the backend's
 * `mapPaymentStatus` emits; anything unrecognised maps to `null` and is preserved in
 * `paymentStatusRaw`.
 */
export enum PaymentStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
}

export enum CancelFeedback {
  TOO_EXPENSIVE = 'too_expensive',
  UNUSED = 'unused',
  SWITCHED_SERVICE = 'switched_service',
  CUSTOMER_SERVICE = 'customer_service',
  LOW_QUALITY = 'low_quality',
  MISSING_FEATURES = 'missing_features',
  OTHER = 'other',
}

// ---------------------------------------------------------------------------
// DTOs (wire shapes)
// ---------------------------------------------------------------------------

export interface SubscriptionDto {
  id: string;
  service_id: string;
  tier: string;
  interval: string;
  seats: number;
  crew_seats: number;
  payment_status: string;
  next_payment_at: string;
  cancel_at_period_end: boolean;
  cancel_at: string;
  canceled_at: string;
  /**
   * The answer the vessel's payer gave to "are you going to add an owner?" — asked only of a
   * captain paying for a vessel that has none. `true` holds one management seat empty for the
   * owner still to come; `false` — the column's default, and what every plan an owner bought
   * carries — hands that seat to the captain.
   */
  captain_will_add_owner: boolean;
  /**
   * Granted by a Vessel Haven admin rather than bought: never billed, never expiring, and
   * closed to every change from the vessel's side — a plan change, checkout, cancel or resume
   * is refused with 409 `SUBSCRIPTION_MANUALLY_GRANTED`.
   */
  manually_granted?: boolean;
}

// `unread` is a generic page-wrapper field reused from notifications — meaningless here.
export interface SubscriptionsPageDto {
  count: number;
  items: SubscriptionDto[];
  unread?: number;
}

export interface CreateCheckoutSubscriptionDto {
  tier: PaidSubscriptionTier;
  interval: BillingInterval;
  seats: number;
  crew_seats?: number;
  /** See `UpdateSubscriptionDto.will_add_owner`. Stored on the row this checkout creates. */
  will_add_owner?: boolean;
}

export interface UpdateSubscriptionDto {
  tier?: PaidSubscriptionTier;
  interval?: BillingInterval;
  seats?: number;
  crew_seats?: number;
  /**
   * Unlike every other field here, an omitted `will_add_owner` does **not** keep its current
   * value. `ChangeSubscription` writes `req.WillAddOwner` unconditionally (the People
   * service's `internal/app/payments/service.go`) and the field is a plain Go `bool`, so a
   * body without it decodes to `false` and clears the answer — moving the reserved management
   * seat off the owner-to-be and onto the captain as a side effect of, say, buying a crew
   * seat. So it is always sent for a vessel the question applies to, never left out.
   */
  will_add_owner?: boolean;
}

export interface CancelSubscriptionDto {
  feedback?: CancelFeedback;
  comment?: string;
}

export interface StripeUrlDto {
  url: string;
}

/**
 * The spec types `amount` as `currency.Amount`, declared as an empty object with no
 * properties. This is the shape the endpoint actually returns.
 */
export interface PriceDto {
  id: string;
  lookup_key: string;
  amount: {
    number: string;
    currency: string;
  };
}

// ---------------------------------------------------------------------------
// Domain models
// ---------------------------------------------------------------------------

export interface Subscription {
  id: string;
  serviceId: string;
  /** `null` when the backend returns a tier this client doesn't know. */
  tier: SubscriptionTier | null;
  interval: BillingInterval | null;
  seats: number;
  /** Add-on count, separate from the billable `seats`. */
  crewSeats: number;
  paymentStatus: PaymentStatus | null;
  /** Unmapped `payment_status`, kept so unknown states stay debuggable. */
  paymentStatusRaw: string;
  /** Next renewal charge. `null` when unset (harbor, or an ended subscription). */
  nextPaymentAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** When access actually ends. */
  cancelAt: string | null;
  /** When the cancel request was made. */
  canceledAt: string | null;
  /**
   * Whether the payer said an owner is still coming, which holds one management seat empty for
   * them. Only meaningful while the vessel has no owner — once one joins, the reserved seat is
   * theirs whatever the answer was.
   */
  captainWillAddOwner: boolean;
  /** A lifetime plan an admin granted — see `SubscriptionDto.manually_granted`. */
  manuallyGranted: boolean;
}

export interface SubscriptionsPage {
  count: number;
  items: Subscription[];
}

export interface Price {
  id: string;
  lookupKey: string;
  amount: number;
  currency: string;
}

/**
 * The service whose `X-Service-Id` header the request carries. Passed explicitly so a
 * vessel-scoped screen can name that vessel rather than relying on session state.
 */
export interface GetSubscriptionParams {
  serviceId: string;
}

export interface GetAllSubscriptionsParams extends GetSubscriptionParams {
  limit?: number;
  page?: number;
}

export interface SubscriptionMutationParams extends GetSubscriptionParams {
  /** Subscription id — the `{id}` in PATCH/cancel/uncancel. */
  id: string;
}

// ---------------------------------------------------------------------------
// Seats
// ---------------------------------------------------------------------------

/**
 * A subscription buys two separate pools — `seats`, the billable management seats, and
 * `crew_seats`, a cheaper add-on. Each pool maps to one `seat_type` per tier, and a person
 * occupies exactly one seat type (or none) through their role's `seat_type` column.
 */
export type SeatPool = 'management' | 'crew';

/**
 * Mirrors `seatCapacity` in the People service's `internal/app/people/helpers.go`: a seat
 * type only exists for the tier it names, and the server answers `ErrSeatTierMismatch` for
 * any other pairing. `harbor` buys no crew pool at all.
 */
const SEAT_TYPE_BY_TIER: Record<SubscriptionTier, Record<SeatPool, SeatType | null>> = {
  [SubscriptionTier.HARBOR]: { management: SeatType.HARBOR_SEAT, crew: null },
  [SubscriptionTier.NAVIGATE]: {
    management: SeatType.NAVIGATE_SEAT,
    crew: SeatType.NAVIGATE_CREW,
  },
  [SubscriptionTier.COMMAND]: { management: SeatType.COMMAND_SEAT, crew: SeatType.COMMAND_CREW },
};

export const seatTypeFor = (tier: SubscriptionTier | null, pool: SeatPool): SeatType | null =>
  tier ? SEAT_TYPE_BY_TIER[tier][pool] : null;

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

const planFields = {
  tier: z.enum(PAID_TIERS, 'Plan is required'),
  interval: z.enum(BillingInterval, 'Billing interval is required'),
  seats: z.number('Seats must be a number').int().min(1, 'At least one seat is required'),
  crewSeats: z.number('Crew seats must be a number').int().min(0).optional(),
  /**
   * Only a captain paying for a vessel that has no owner is asked this; the server ignores it
   * for anybody else, since the reserved seat is the owner's whatever the answer. Optional
   * because the form only carries it where the question was put — see
   * `UpdateSubscriptionDto.will_add_owner` for why it is always *sent* once it applies.
   */
  willAddOwner: z.boolean().optional(),
};

export const createCheckoutSubscriptionSchema = z.object(planFields);

export type CreateCheckoutSubscription = z.infer<typeof createCheckoutSubscriptionSchema>;

/**
 * Every field is optional — omitted fields keep their current value, so `{ seats: 5 }`
 * changes only the seat count. One exception the caller must honour: leaving `harbor`
 * requires tier + interval + seats together (there is no existing plan to fall back on)
 * and answers 200 + a Stripe URL instead of 204.
 */
export const updateSubscriptionSchema = z.object({
  tier: planFields.tier.optional(),
  interval: planFields.interval.optional(),
  seats: planFields.seats.optional(),
  crewSeats: planFields.crewSeats,
  willAddOwner: planFields.willAddOwner,
});

export type UpdateSubscription = z.infer<typeof updateSubscriptionSchema>;

export const cancelSubscriptionSchema = z.object({
  feedback: z.enum(CancelFeedback).optional(),
  comment: z.string().optional(),
});

export type CancelSubscription = z.infer<typeof cancelSubscriptionSchema>;
